/**
 * OPERATION MULTI-TIERS — Frontend feature-flag hook.
 *
 * Single source of truth for what the current user can see/access.
 * Calls GET /api/user/features once after auth loads, caches in memory,
 * and exposes a stable object for conditional rendering.
 *
 * Fails safe: on error the user gets the FULL feature set (Quebec tier),
 * so we never lock an existing customer out due to a bad request.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth, api } from '../context/AppContext';

const FULL_ACCESS = {
  service_tier: 'quebec',
  is_privileged: false,
  has_grocery_deals: true,
  has_human_coach: true,
  has_messaging: true,
  has_recipes_v2: false,
  locale: 'fr',
};

export function useFeatures() {
  const { user } = useAuth();
  const [features, setFeatures] = useState(FULL_ACCESS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setFeatures(FULL_ACCESS);
      setLoading(false);
      return;
    }
    try {
      const [featRes, onbRes] = await Promise.all([
        api.get('/user/features'),
        // Iter. 406.p — onboarding tunnel state used by Layout to gate
        // the "Activer mon cycle 6 semaines" nav entry.
        api.get('/onboarding/state').catch(() => null),
      ]);
      const onb = onbRes?.data || {};
      setFeatures({
        ...FULL_ACCESS,
        ...(featRes.data || {}),
        has_active_cycle: !!onb.has_active_cycle,
        welcome_completed: !!onb.welcome_completed,
      });
    } catch {
      // Fail safe: keep full access on error so existing users aren't locked out.
      setFeatures(FULL_ACCESS);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  return { features, loading, refresh: load };
}
